import { Button } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Popper from '@material-ui/core/Popper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Iterable } from 'immutable';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import config from 'config';
import Api from 'entities/api';
import Budget from 'entities/budget/model';
import BudgetAllocation from 'entities/budgetAllocation/model';
import DonationCreditRule from 'entities/donationCreditRule/model';
import DonationMatchingRule from 'entities/donationMatchingRule/model';
import { selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import { lightGray, primaryBlue } from 'utils/color-styles';
import { Features } from 'utils/features';
import {
  CORPORATE_DONATION_URL_PATH,
  VOLUNTEER_REWARD_BUDGET_ALLOCATION,
  VOLUNTEER_REWARD_URL_PATH,
} from 'entities/budgetAllocation/constants';

import BudgetAllocationCompact from './BudgetAllocationCompact';
import DonationCreditRuleCompact from './DonationCreditRuleCompact';
import DonationGivingRulesBudgetSummary from './DonationGivingRulesBudgetSummary';
import DonationMatchingRuleCompact from './DonationMatchingRuleCompact';
import { summarizeBudget } from './utils';

type Rule = DonationCreditRule | DonationMatchingRule | BudgetAllocation;
type DateRangeable = Budget | Rule;

const sortDateRangeable = (a: DateRangeable, b: DateRangeable) => {
  if (!a.get('endingAt') && !b.get('endingAt')) {
    return b.get('createdAt') - a.get('createdAt');
  }
  if (!a.get('endingAt')) {
    return -1;
  }
  if (!b.get('endingAt')) {
    return 1;
  }
  return b.get('endingAt') - a.get('endingAt');
};

interface DonationGivingRulesForBudgetProps {
  budget: Budget;
  rulesList: Iterable<string, Rule>;
}

const DonationGivingRulesForBudget = ({
  budget,
  rulesList,
}: DonationGivingRulesForBudgetProps): JSX.Element => {
  const user = useSelector(selectUser);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const {
    budgetRules: budgetDonationRules,
    budgetTotals,
    budgetInactive: inactive,
    budgetBalanceLow: balanceLow,
  } = summarizeBudget(budget, rulesList);

  const showContributionAmount =
    user?.hasFeature(Features.BUDGET_LIMIT_ASSERTION_TYPE) &&
    budget.get('limitAssertionType') === 'Contribution';

  return (
    <Box
      key={budget.get('id')}
      style={{
        paddingTop: 10,
        marginBottom: 40,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h2 style={{ opacity: inactive ? '0.5' : '1' }}>{budget.get('title')}</h2>
        {user?.hasFeature(Features.DONATION_CREDIT) ? (
          // eslint-disable-next-line max-len
          <a // eslint-disable-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
            style={{
              marginLeft: 30,
              color: primaryBlue,
              textDecoration: 'underline',
            }}
            onClick={(e) => {
              const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;

              if (!a.href) {
                fetch(`${config.apiUrl}/api/budgets/${budget.id}/donationCreditRules/report`, {
                  method: 'GET',
                  headers: {
                    Authorization: Api.defaultHeaders.Authorization,
                  },
                })
                  .then((response) => response.blob())
                  .then((blob) => {
                    const url = window.URL.createObjectURL(blob);
                    a.href = url;
                    a.download = `${budget.title}--donationCredit-${new Date().toISOString()}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.removeAttribute('href');
                  });
              }
            }}
            role="button"
            tabIndex="0"
            title="Download donation credits report"
          >
            Download donation credits report
          </a>
        ) : null}
        <Link
          to={`/donations/budgets/${budget.id}`}
          style={{
            marginLeft: 30,
            color: primaryBlue,
            textDecoration: 'underline',
          }}
        >
          Edit
        </Link>
      </div>
      <DonationGivingRulesBudgetSummary
        budget={budget}
        budgetTotals={budgetTotals}
        budgetBalanceLow={balanceLow}
        budgetInactive={inactive}
      />
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: inactive ? '0.5' : '1',
          }}
        >
          <h3>Rules</h3>
          <div style={{ marginLeft: 'auto', marginRight: 15 }}>
            <div
              style={{
                textAlign: 'right',
                display: 'flex',
                alignItems: 'middle',
              }}
            >
              {[
                user?.hasFeature(Features.DONATION_MATCHING),
                user?.hasFeature(Features.DONATION_CREDIT),
                user?.hasFeature(Features.CORPORATE_DONATIONS),
                user?.hasFeature(Features.VOLUNTEER_REWARD),
              ].filter(Boolean).length > 0 && (
                <>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleClick}
                    style={{ paddingLeft: 20, lineHeight: '24px' }}
                  >
                    Create New Rule <ArrowDropDownIcon />
                  </Button>
                  <Popper
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    style={{ marginTop: 5, width: '220px' }}
                  >
                    <Box style={{ padding: 0 }}>
                      <ClickAwayListener onClickAway={() => handleClick(false)}>
                        <MenuList disablePadding>
                          {user?.hasFeature(Features.DONATION_MATCHING) && (
                            <MenuItem>
                              <Link to={`/donations/matching-rule/new/${budget.id}`}>
                                <div>Matching</div>
                              </Link>
                            </MenuItem>
                          )}
                          {user?.hasFeature(Features.DONATION_CREDIT) && (
                            <MenuItem
                              style={{
                                borderTop: `1px solid ${lightGray}`,
                              }}
                            >
                              <Link to={`/donations/credit-rule/new/${budget.id}`}>
                                <div>Donation Credit</div>
                              </Link>
                            </MenuItem>
                          )}
                          {(user?.hasFeature(Features.CORPORATE_DONATIONS) ||
                            user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS)) && (
                            <MenuItem
                              style={{
                                borderTop: `1px solid ${lightGray}`,
                              }}
                            >
                              <Link
                                to={`/donations/corporate-donation-allocation/new/${budget.id}`}
                              >
                                <div>Corporate Donation</div>
                              </Link>
                            </MenuItem>
                          )}
                          {user?.hasFeature(Features.VOLUNTEER_REWARD) && (
                            <MenuItem>
                              <Link to={`/donations/${VOLUNTEER_REWARD_URL_PATH}/new/${budget.id}`}>
                                <div>Volunteer Grants</div>
                              </Link>
                            </MenuItem>
                          )}
                        </MenuList>
                      </ClickAwayListener>
                    </Box>
                  </Popper>
                </>
              )}
            </div>
          </div>
        </div>
        {budgetDonationRules
          .toArray()
          .sort(sortDateRangeable)
          .map((rule) => {
            if (rule instanceof DonationCreditRule) {
              return (
                <DonationCreditRuleCompact
                  key={rule.get('id')}
                  donationCreditRule={rule}
                  budgetInactive={inactive}
                />
              );
            }

            if (rule instanceof DonationMatchingRule) {
              return (
                <DonationMatchingRuleCompact
                  key={rule.get('id')}
                  donationMatchingRule={rule}
                  budgetInactive={inactive}
                  showContributionAmount={showContributionAmount}
                />
              );
            }

            if (rule instanceof BudgetAllocation) {
              const volunteerRewardRule = rule.get('__t') === VOLUNTEER_REWARD_BUDGET_ALLOCATION;
              const shouldShowVolunteerRewards = user?.hasFeature(Features.VOLUNTEER_REWARD);
              if (volunteerRewardRule && !shouldShowVolunteerRewards) {
                return null;
              }
              return (
                <BudgetAllocationCompact
                  key={rule.get('id')}
                  budgetAllocation={rule}
                  budgetInactive={inactive}
                  primaryLabel={volunteerRewardRule ? 'Volunteer Grants' : 'Corporate Donations'}
                  urlPath={
                    volunteerRewardRule ? VOLUNTEER_REWARD_URL_PATH : CORPORATE_DONATION_URL_PATH
                  }
                  donationsCount={
                    volunteerRewardRule
                      ? rule.get('volunteerRewardDonationsCount')
                      : rule.get('corporateDonationsCount')
                  }
                  extraMatchingGrantsFields={volunteerRewardRule}
                />
              );
            }

            return null;
          })}
      </div>
    </Box>
  );
};

interface DonationGivingRulesListProps {
  budgets: Iterable<string, Budget>;
  rulesList: Iterable<string, Rule>;
}

const DonationGivingRulesList: React.FC<DonationGivingRulesListProps> = ({
  budgets,
  rulesList,
}) => (
  <>
    {budgets
      .sort(sortDateRangeable)
      .toArray()
      .filter(Boolean)
      .map((budget) => (
        <DonationGivingRulesForBudget
          key={budget.get('id')}
          budget={budget}
          rulesList={rulesList}
        />
      ))}
  </>
);

export default DonationGivingRulesList;
